import axios from "axios";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { toast } from "../hooks/use-toast";
import ROUTES from "../utils/ROUTES";

export interface ApiResponse<T> {
  data: T;
  success: boolean;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

const ACCESS_TOKEN_KEY = "access_token";
const LOGOUT_REDIRECT_DELAY_MS = 4000;
let isHandlingAuthError = false;

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if ((status === 401 || status === 403) && !isHandlingAuthError) {
      isHandlingAuthError = true;
      localStorage.removeItem(ACCESS_TOKEN_KEY);

      toast({
        variant: "error",
        title: status === 403 ? "Access denied" : "Session expired",
        description:
          status === 403
            ? "You do not have sufficient permission level - login as an administrator"
            : "Your session has expired. Please log in again.",
      });

      try {
        await signOut(auth);
      } catch {
        // ignore - we're redirecting to login regardless
      }

      const basename = process.env.REACT_APP_BASENAME || "";
      setTimeout(() => {
        window.location.href = `${basename}${ROUTES.LOGIN}`;
      }, LOGOUT_REDIRECT_DELAY_MS);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
